import React, {FC, memo, useMemo, useState} from "react";
import {Button, ButtonProps, Drawer, Skeleton, Tooltip} from "antd";
import {HistoryOutlined, SyncOutlined} from "@ant-design/icons";
import {ApolloError} from "apollo-client";
import {
    Course,
    PaymentInterval,
    useCourseHistoryByCourseIdQuery,
    useGetCourseTypesQuery,
    useGetLocationsQuery,
} from "../../generated/graphql";
import useHighestRole from "../../helpers/useHighestRole";
import Roles from "../../../shared/models/roles";
import {atLeast} from "../common/AuthenticateSetup";
import CourseHistoryContent, {EntityLookupMaps} from "./CourseHistoryContent";
import css from "../bookingHistory/BookingHistory.less";

type CourseHistoryDrawerProps = {
    courseId?: Course["id"];
    courseNumber?: string;
    buttonProps?: ButtonProps;
    buttonStyle?: React.CSSProperties;
    tooltipPlacement?: "left" | "right" | "top" | "bottom";
};

/**
 * Drawer-Komponente für die Kurs-Historie (Änderungen am Kurs selbst)
 * Zeigt alle Änderungen an Kursdaten, Kurseinheiten, Preisen und Ratenzahlungen
 */
const CourseHistoryDrawer: FC<CourseHistoryDrawerProps> = ({courseId, courseNumber, buttonProps, buttonStyle, tooltipPlacement = "left"}) => {
    const highestRole = useHighestRole();
    const [open, setOpen] = useState(false);
    const [refetching, setRefetching] = useState(false);

    // Admin und hoeher haben Zugriff (hierarchie-basiert, deckungsgleich mit dem
    // Backend @Authorized([Admin, Superuser]) auf courseHistoryByCourseId).
    const hasAccess = atLeast(Roles.Admin).includes(highestRole);

    const {data, loading, error, refetch} = useCourseHistoryByCourseIdQuery({
        skip: !courseId || !open || !hasAccess,
        variables: {courseId: courseId ?? ""},
        fetchPolicy: "network-only",
    });

    // Lade CourseTypes und Locations fuer UUID-Aufloesung im Detail-Modal
    const {data: courseTypesData} = useGetCourseTypesQuery({
        skip: !open || !hasAccess,
    });

    const {data: locationsData} = useGetLocationsQuery({
        skip: !open || !hasAccess,
    });

    /**
     * Erstellt Lookup-Maps fuer die Aufloesung von UUIDs zu lesbaren Namen
     * - courseTypeId -> Kursart-Name
     * - locationId -> Standort-Name
     * - venueId -> Raum/Becken-Name
     */
    const lookupMaps = useMemo((): EntityLookupMaps => {
        const courseTypes: Record<string, string> = {};
        const locations: Record<string, string> = {};
        const venues: Record<string, string> = {};

        // CourseTypes Map aufbauen
        if (courseTypesData?.courseTypes) {
            courseTypesData.courseTypes.forEach((ct) => {
                courseTypes[ct.id] = ct.name;
            });
        }

        // Locations und Venues Maps aufbauen
        if (locationsData?.locations) {
            locationsData.locations.forEach((loc) => {
                locations[loc.id] = loc.name;
                // Venues sind in Locations verschachtelt
                if (loc.venues) {
                    loc.venues.forEach((venue) => {
                        venues[venue.id] = venue.name;
                    });
                }
            });
        }

        return {courseTypes, locations, venues};
    }, [courseTypesData, locationsData]);

    const history = data?.courseHistoryByCourseId ?? [];

    // Extrahiere Kurs-Metadaten aus dem ersten History-Item (alle Items haben denselben Kurs)
    const courseFromHistory = history[0]?.course;
    const lessonsCount = courseFromHistory?.lessons?.length ?? 0;
    const currentPaymentInterval = courseFromHistory?.paymentInterval ?? undefined;

    if (!hasAccess) {
        return null;
    }

    if (!open) {
        return (
            <Tooltip title="Kurs-Änderungshistorie anzeigen" placement={tooltipPlacement}>
                <Button {...buttonProps} onClick={() => setOpen(true)} icon={<HistoryOutlined style={{fontSize: buttonStyle?.fontSize}} />} style={buttonStyle} />
            </Tooltip>
        );
    }

    const LoadingContent = ({loading: isLoading}: {loading: boolean}) => {
        if (!isLoading) {
            return null;
        }

        return (
            <div>
                <Skeleton active />
                <Skeleton active />
                <Skeleton active />
            </div>
        );
    };

    const ErrorContent = ({error: err}: {error?: Error}) => {
        if (!err) {
            return null;
        }

        return (
            <div style={{color: "red", padding: 16}}>
                Fehler beim Laden der Historie: {err.message}
            </div>
        );
    };

    const handleRefetch = async () => {
        setRefetching(true);
        await refetch();
        setRefetching(false);
    };

    const RefetchButton = () => {
        return <Button icon={<SyncOutlined spin={loading || refetching} />} type="link" onClick={handleRefetch} />;
    };

    const drawerTitle = courseNumber ? `Kurs-Historie: ${courseNumber}` : "Kurs-Historie";

    return (
        <>
            <Tooltip title="Kurs-Änderungshistorie anzeigen" placement={tooltipPlacement}>
                <Button {...buttonProps} onClick={() => setOpen(true)} icon={<HistoryOutlined style={{fontSize: buttonStyle?.fontSize}} />} style={buttonStyle} />
            </Tooltip>
            <Drawer
                title={drawerTitle}
                open={open}
                onClose={() => setOpen(false)}
                destroyOnClose
                extra={<RefetchButton />}
                width="600px"
                className={css.historyDrawer}
            >
                <div>
                    <LoadingContent loading={loading || refetching} />
                    <ErrorContent error={error instanceof ApolloError ? error : undefined} />
                    {!loading && !refetching && !error && (
                        <CourseHistoryContent
                            history={history}
                            lookupMaps={lookupMaps}
                            lessonsCount={lessonsCount}
                            currentPaymentInterval={currentPaymentInterval}
                        />
                    )}
                </div>
            </Drawer>
        </>
    );
};

export default memo(CourseHistoryDrawer);
